import { Link, Navigate } from 'react-router-dom'
import { FileText, GitMerge, Wrench } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import {
  CoverageNotice,
  EmptyState,
  ErrorState,
  LoadingState,
  RankedBars,
  StatTile,
  UnavailableNotice,
  humanize,
} from '../components/DataViz'
import { fetchEngineeringBoard, type EngineeringHandoff } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'

/**
 * Engineering (AFOS-41): every support case a named person handed to engineering,
 * joined to its squad run, Jira proposals (status only; never published by AFOS),
 * and reviewed outcome; plus engineering runs that did not start from a case.
 */
export function EngineeringPage() {
  const { user, loading } = useAuth()
  const board = useAsync(fetchEngineeringBoard, !!user)

  if (!loading && !user) return <Navigate to="/signin" replace />
  const data = board.data

  const stageBars = (() => {
    const counts: Record<string, number> = {}
    for (const h of data?.handoffs ?? []) counts[h.state] = (counts[h.state] ?? 0) + 1
    return Object.entries(counts).map(([k, v]) => ({ key: k, label: humanize(k), value: v, href: '/cases' }))
  })()

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Engineering</h1>
          <p className="afos-page-lead">
            From support case to merged fix: the handoff decision, the squad run, the proposed work
            item, and the reviewed outcome, on one board.
          </p>
        </header>

        {board.error && !data && (
          <ErrorState
            message={
              /support\.draft/.test(board.error)
                ? 'The engineering board shows support case data. Ask an admin for a role with support access.'
                : board.error
            }
            onRetry={() => void board.reload()}
          />
        )}
        {!data && !board.error && <LoadingState label="Loading the engineering board" />}

        {data && (
          <>
            <UnavailableNotice sources={data.unavailable} />
            <CoverageNotice incomplete={data.incomplete} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                emphasis
                label="Handed to engineering"
                value={data.totals.handoffs}
                icon={<Wrench aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
              />
              <StatTile label="With a squad run" value={data.totals.with_run} />
              <StatTile
                label="Merge approved"
                value={data.totals.merge_approved}
                hint="Approved by a named person; an operator performs the Git merge"
                icon={<GitMerge aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
              />
              <StatTile
                label="Resolved and reviewed"
                value={data.totals.resolved}
                icon={<FileText aria-hidden="true" className="h-5 w-5 text-[var(--color-cyan)]" />}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <RankedBars
                  title="Handoffs by stage"
                  data={stageBars}
                  empty="No case has been handed to engineering yet."
                />
              </div>
              <div className="lg:col-span-2">
                {data.handoffs.length === 0 ? (
                  <EmptyState>
                    No handoffs yet. When a support lead decides a case needs engineering in{' '}
                    <Link to="/cases" className="text-[var(--color-cyan)] hover:underline">
                      Cases
                    </Link>
                    , it appears here with its run and outcome.
                  </EmptyState>
                ) : (
                  <div className="glass afos-scroll-x rounded-3xl">
                    <table className="w-full min-w-[40rem] text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-mist)]">
                          <th className="px-5 py-3 font-semibold">Case</th>
                          <th className="px-3 py-3 font-semibold">Decision</th>
                          <th className="px-3 py-3 font-semibold">Run</th>
                          <th className="px-3 py-3 font-semibold">Work item</th>
                          <th className="px-5 py-3 font-semibold">Outcome</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.handoffs.map((h) => (
                          <HandoffRow key={h.case_id} h={h} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <section aria-labelledby="runs-no-case" className="space-y-3">
              <h2 id="runs-no-case" className="font-[family-name:var(--font-display)] text-xl font-extrabold">
                Runs not started from a case
              </h2>
              {data.runs_without_case.length === 0 ? (
                <EmptyState compact>Every engineering run traces back to a support case.</EmptyState>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {data.runs_without_case.slice(0, 20).map((r) => (
                    <li key={r.run_id}>
                      <Link
                        to={`/tickets?run=${encodeURIComponent(r.run_id)}`}
                        className="glass block rounded-2xl px-4 py-3 hover:bg-white/5"
                      >
                        <div className="truncate text-sm font-semibold text-[var(--color-foam)]">
                          {r.subject || r.run_id}
                        </div>
                        <div className="text-xs text-[var(--color-mist)]">
                          {humanize(r.status || 'unknown')}
                          {r.stage ? ` · ${r.stage}` : ''}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </AppChrome>
  )
}

function HandoffRow({ h }: { h: EngineeringHandoff }) {
  return (
    <tr className="border-t border-[var(--color-line)] align-top">
      <td className="px-5 py-3">
        <Link
          to={`/cases?case=${encodeURIComponent(h.case_id)}`}
          className="font-semibold text-[var(--color-foam)] hover:text-[var(--color-cyan)]"
        >
          {h.ticket || h.case_id}
        </Link>
        <div className="text-xs text-[var(--color-mist)]">{h.source_system || 'support'}</div>
      </td>
      <td className="px-3 py-3 text-[var(--color-foam)]">
        {humanize(h.decision)}
        {h.decision_stale && (
          <div className="text-xs text-[var(--color-amber)]">Stale: packet changed</div>
        )}
      </td>
      <td className="px-3 py-3">
        {h.run_id ? (
          <Link to={`/tickets?run=${encodeURIComponent(h.run_id)}`} className="text-[var(--color-foam)] hover:text-[var(--color-cyan)]">
            {humanize(h.run_status || 'unknown')}
            {h.merge_approved ? ' · merge approved' : ''}
          </Link>
        ) : (
          <span className="text-[var(--color-mist)]">Not started</span>
        )}
      </td>
      <td className="px-3 py-3 text-[var(--color-mist)]">
        {h.proposals ? `${h.proposals} proposal${h.proposals === 1 ? '' : 's'} (${h.proposal_statuses.join(', ')})` : 'None'}
      </td>
      <td className="px-5 py-3 text-[var(--color-foam)]">
        {h.outcome_status ? humanize(h.outcome_status) : <span className="text-[var(--color-mist)]">Pending</span>}
        {h.outcome_stale && <div className="text-xs text-[var(--color-amber)]">Retracted for re-review</div>}
      </td>
    </tr>
  )
}
